import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';
import { remixCreateTerminalOwnership } from './remix-create-terminal-ownership';

export const REMIX_INSERT_RENDER_TIMEOUT_MS = 35_000;

export type RemixInsertRenderIdentity = {
	type: 'localId' | 'mediaId';
	value: string;
};

type RenderOutcome = { type: 'succeeded' } | { error: Error; reason: string; type: 'failed' };

type PendingVerification = {
	attributes?: Record<string, unknown>;
	experienceTracker: ExperienceTrackerAPI;
	timeout?: ReturnType<typeof setTimeout>;
};

const pendingVerifications = new Map<string, PendingVerification>();
const recentOutcomes = new Map<
	string,
	{ outcome: RenderOutcome; timeout: ReturnType<typeof setTimeout> }
>();

const toKey = ({ type, value }: RemixInsertRenderIdentity): string => `${type}:${value}`;

const settle = (pending: PendingVerification, outcome: RenderOutcome): void => {
	if (pending.timeout) {
		clearTimeout(pending.timeout);
	}
	try {
		if (outcome.type === 'succeeded') {
			pending.experienceTracker.succeed({
				name: ExperienceName.REMIX_INSERT,
				attributes: pending.attributes,
			});
			return;
		}

		pending.experienceTracker.fail({
			name: ExperienceName.REMIX_INSERT,
			error: outcome.error,
			attributes: {
				...pending.attributes,
				renderFailureReason: outcome.reason,
			},
		});
	} catch {
		// Reliability telemetry must never affect the inserted content's render path.
	}
};

const recordOutcome = (identity: RemixInsertRenderIdentity, outcome: RenderOutcome): void => {
	const key = toKey(identity);
	const pending = pendingVerifications.get(key);
	if (pending) {
		pendingVerifications.delete(key);
		settle(pending, outcome);
		return;
	}

	const previous = recentOutcomes.get(key);
	if (previous) {
		clearTimeout(previous.timeout);
	}
	const timeout = setTimeout(() => recentOutcomes.delete(key), REMIX_INSERT_RENDER_TIMEOUT_MS);
	recentOutcomes.set(key, { outcome, timeout });
};

/**
 * Transfers an already-started aiMateRemixInsert experience to the render lifecycle.
 * Render notifications may arrive before the editor insertion acknowledgement, so
 * recent outcomes are retained briefly and consumed here.
 */
export const beginRemixInsertRenderVerification = ({
	attributes,
	experienceTracker,
	identity,
}: {
	attributes?: Record<string, unknown>;
	experienceTracker: ExperienceTrackerAPI;
	identity: RemixInsertRenderIdentity;
}): void => {
	const remixCreateId = attributes?.remixCreateId;
	if (typeof remixCreateId === 'string') {
		remixCreateTerminalOwnership.commit(remixCreateId);
	}
	const key = toKey(identity);
	const recent = recentOutcomes.get(key);
	if (recent) {
		clearTimeout(recent.timeout);
		recentOutcomes.delete(key);
		settle({ attributes, experienceTracker }, recent.outcome);
		return;
	}

	const previous = pendingVerifications.get(key);
	if (previous) {
		if (previous.timeout) {
			clearTimeout(previous.timeout);
		}
	}
	const timeout = setTimeout(() => {
		const pending = pendingVerifications.get(key);
		if (!pending) {
			return;
		}
		pendingVerifications.delete(key);
		settle(pending, {
			type: 'failed',
			reason: 'render_timeout',
			error: new Error('Inserted Remix content did not render within the expected time'),
		});
	}, REMIX_INSERT_RENDER_TIMEOUT_MS);
	pendingVerifications.set(key, { attributes, experienceTracker, timeout });
};

export const completeRemixInsertRenderVerification = (identity: RemixInsertRenderIdentity): void =>
	recordOutcome(identity, { type: 'succeeded' });

export const failRemixInsertRenderVerification = ({
	error,
	identity,
	reason,
}: {
	error: Error;
	identity: RemixInsertRenderIdentity;
	reason: string;
}): void => recordOutcome(identity, { error, reason, type: 'failed' });

export const resetRemixInsertRenderVerificationsForTesting = (): void => {
	for (const pending of pendingVerifications.values()) {
		if (pending.timeout) {
			clearTimeout(pending.timeout);
		}
	}
	for (const recent of recentOutcomes.values()) {
		clearTimeout(recent.timeout);
	}
	pendingVerifications.clear();
	recentOutcomes.clear();
	remixCreateTerminalOwnership.reset();
};
